import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import VertragsartPicker from "./VertragsartPicker";
import TarifierungPicker from "./TarifierungPicker";
import {
  VERSICHERER_OPTIONS,
  type TarifierungKey,
  type VersichererKey,
  type VertragsartKey,
  type VorvertragInfo,
} from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  block: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  blockTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  blockSub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  yesNoRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    maxWidth: "380px",
  },
  ynTile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "6px",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "84px",
    textAlign: "center",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1.5px", "solid", BRAND.border),
    transitionProperty: "border-color, background-color, transform",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-1px)",
    },
  },
  ynActiveYes: {
    backgroundColor: BRAND.pillarGreen,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.pillarGreen),
    boxShadow: "0 0 0 3px rgba(34,128,79,0.18)",
  },
  ynActiveNo: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: "0 0 0 3px rgba(77,32,81,0.18)",
  },
  ynIcon: {
    width: "26px",
    height: "26px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  followUpGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 720px)": { gridTemplateColumns: "1fr 1fr 1fr" },
  },
  field: { display: "flex", flexDirection: "column", rowGap: "4px" },
  fieldLabel: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.muted,
    letterSpacing: "0.03em",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  versichererRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "10px",
    rowGap: "10px",
  },
  versichererTile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "18px",
    paddingRight: "18px",
    minWidth: "160px",
    textAlign: "center",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1.5px", "solid", BRAND.border),
    transitionProperty: "border-color, background-color, transform",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-1px)",
    },
  },
  versichererTileActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: "0 0 0 3px rgba(77,32,81,0.18)",
  },
  buendelHint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: "#fff5e6",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
  },
});

type Props = {
  vorvertrag: VorvertragInfo;
  versicherer: VersichererKey;
  vertragsart: VertragsartKey | "";
  tarifierung: TarifierungKey;
  onVorvertragChange: (next: VorvertragInfo) => void;
  onVersichererChange: (next: VersichererKey) => void;
  onVertragsartChange: (next: VertragsartKey) => void;
  onTarifierungChange: (next: TarifierungKey) => void;
};

const VertragsBasisSection = ({
  vorvertrag,
  versicherer,
  vertragsart,
  tarifierung,
  onVorvertragChange,
  onVersichererChange,
  onVertragsartChange,
  onTarifierungChange,
}: Props) => {
  const styles = useStyles();
  const setVorvertrag = (next: "ja" | "nein") => {
    if (vorvertrag.exists === next) {
      onVorvertragChange({ exists: "" });
      return;
    }
    if (next === "nein") {
      onVorvertragChange({ exists: "nein" });
      return;
    }
    onVorvertragChange({ ...vorvertrag, exists: next });
  };
  const setFremdvertrag = (next: "ja" | "nein") => {
    if (vorvertrag.isFremdvertrag === next) {
      onVorvertragChange({ ...vorvertrag, isFremdvertrag: "" });
      return;
    }
    onVorvertragChange({ ...vorvertrag, isFremdvertrag: next });
  };
  const setAlreadyCancelled = (next: "ja" | "nein") => {
    if (vorvertrag.alreadyCancelled === next) {
      onVorvertragChange({ ...vorvertrag, alreadyCancelled: "" });
      return;
    }
    onVorvertragChange({ ...vorvertrag, alreadyCancelled: next });
  };
  const setShouldCancel = (next: "ja" | "nein") => {
    if (vorvertrag.shouldCancel === next) {
      onVorvertragChange({ ...vorvertrag, shouldCancel: "" });
      return;
    }
    onVorvertragChange({ ...vorvertrag, shouldCancel: next });
  };

  return (
    <div className={styles.root}>
      {/* Vorvertrag */}
      <div className={styles.block}>
        <h3 className={styles.blockTitle}>Gibt es einen Vorvertrag?</h3>
        <span className={styles.blockSub}>
          Bei „Ja" klären wir, ob es sich um einen Fremdvertrag oder einen bereits
          bestehenden eigenen Vertrag handelt.
        </span>
        <div className={styles.yesNoRow}>
          <button
            type="button"
            onClick={() => setVorvertrag("ja")}
            className={mergeClasses(styles.ynTile, (vorvertrag.exists === "ja") && styles.ynActiveYes)}
            aria-pressed={vorvertrag.exists === "ja"}
          >
            <span className={styles.ynIcon}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12l4 4 10-10" />
              </svg>
            </span>
            <span>Ja, Vorvertrag vorhanden</span>
          </button>
          <button
            type="button"
            onClick={() => setVorvertrag("nein")}
            className={mergeClasses(styles.ynTile, (vorvertrag.exists === "nein") && styles.ynActiveNo)}
            aria-pressed={vorvertrag.exists === "nein"}
          >
            <span className={styles.ynIcon}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 5l12 12M17 5L5 17" />
              </svg>
            </span>
            <span>Nein, Neuvertrag</span>
          </button>
        </div>

        {vorvertrag.exists === "ja" && (
          <div style={{ marginTop: 16 }}>
            <h3 className={styles.blockTitle}>Ist es ein Fremdvertrag?</h3>
            <div className={styles.yesNoRow} style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setFremdvertrag("ja")}
                className={mergeClasses(styles.ynTile, (vorvertrag.isFremdvertrag === "ja") && styles.ynActiveYes)}
                aria-pressed={vorvertrag.isFremdvertrag === "ja"}
              >
                <span>Ja, Fremdvertrag</span>
              </button>
              <button
                type="button"
                onClick={() => setFremdvertrag("nein")}
                className={mergeClasses(styles.ynTile, (vorvertrag.isFremdvertrag === "nein") && styles.ynActiveNo)}
                aria-pressed={vorvertrag.isFremdvertrag === "nein"}
              >
                <span>Nein, eigener Vertrag</span>
              </button>
            </div>

            {vorvertrag.isFremdvertrag === "nein" && (
              <div className={styles.field} style={{ maxWidth: 480, marginTop: 12 }}>
                <span className={styles.fieldLabel}>Bestehenden Vertrag auswählen</span>
                <input
                  className={styles.input}
                  type="text"
                  value={vorvertrag.existingContractId ?? ""}
                  placeholder="Vertragsnummer oder Vertragsname"
                  onChange={(e) =>
                    onVorvertragChange({
                      ...vorvertrag,
                      existingContractId: e.target.value,
                    })
                  }
                />
              </div>
            )}

            {vorvertrag.isFremdvertrag === "ja" && (
              <>
                <div className={styles.followUpGrid} style={{ marginTop: 12 }}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Vorversicherer (Gesellschaft)</span>
                    <input
                      className={styles.input}
                      type="text"
                      value={vorvertrag.gesellschaft ?? ""}
                      placeholder="z. B. Generali"
                      onChange={(e) =>
                        onVorvertragChange({ ...vorvertrag, gesellschaft: e.target.value })
                      }
                    />
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Vorvertragsnummer</span>
                    <input
                      className={styles.input}
                      type="text"
                      value={vorvertrag.vertragsnummer ?? ""}
                      placeholder="Versicherungsschein-Nr."
                      onChange={(e) =>
                        onVorvertragChange({ ...vorvertrag, vertragsnummer: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <h3 className={styles.blockTitle}>Ist der Vertrag schon gekündigt?</h3>
                  <div className={styles.yesNoRow} style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={() => setAlreadyCancelled("ja")}
                      className={mergeClasses(styles.ynTile, (vorvertrag.alreadyCancelled === "ja") && styles.ynActiveYes)}
                      aria-pressed={vorvertrag.alreadyCancelled === "ja"}
                    >
                      <span>Ja, bereits gekündigt</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAlreadyCancelled("nein")}
                      className={mergeClasses(styles.ynTile, (vorvertrag.alreadyCancelled === "nein") && styles.ynActiveNo)}
                      aria-pressed={vorvertrag.alreadyCancelled === "nein"}
                    >
                      <span>Nein, noch nicht</span>
                    </button>
                  </div>

                  {vorvertrag.alreadyCancelled === "ja" && (
                    <div className={styles.followUpGrid} style={{ marginTop: 12 }}>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Kündigung zum</span>
                        <input
                          className={styles.input}
                          type="date"
                          value={vorvertrag.kuendigungZum ?? ""}
                          onChange={(e) =>
                            onVorvertragChange({ ...vorvertrag, kuendigungZum: e.target.value })
                          }
                        />
                      </div>
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Kündigungsgrund</span>
                        <input
                          className={styles.input}
                          type="text"
                          value={vorvertrag.kuendigungsgrund ?? ""}
                          placeholder="z. B. ordentliche Kündigung"
                          onChange={(e) =>
                            onVorvertragChange({
                              ...vorvertrag,
                              kuendigungsgrund: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}

                  {vorvertrag.alreadyCancelled === "nein" && (
                    <div style={{ marginTop: 12 }}>
                      <h3 className={styles.blockTitle}>Soll der Vertrag gekündigt werden?</h3>
                      <div className={styles.yesNoRow} style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          onClick={() => setShouldCancel("ja")}
                          className={mergeClasses(styles.ynTile, (vorvertrag.shouldCancel === "ja") && styles.ynActiveYes)}
                          aria-pressed={vorvertrag.shouldCancel === "ja"}
                        >
                          <span>Ja, kündigen</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShouldCancel("nein")}
                          className={mergeClasses(styles.ynTile, (vorvertrag.shouldCancel === "nein") && styles.ynActiveNo)}
                          aria-pressed={vorvertrag.shouldCancel === "nein"}
                        >
                          <span>Nein, weiterlaufen</span>
                        </button>
                      </div>

                      {vorvertrag.shouldCancel === "ja" && (
                        <div className={styles.field} style={{ maxWidth: 280, marginTop: 12 }}>
                          <span className={styles.fieldLabel}>Zu wann?</span>
                          <input
                            className={styles.input}
                            type="date"
                            value={vorvertrag.kuendigungZum ?? ""}
                            onChange={(e) =>
                              onVorvertragChange({
                                ...vorvertrag,
                                kuendigungZum: e.target.value,
                              })
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Vertragsart */}
      <div className={styles.block}>
        <h3 className={styles.blockTitle}>Vertragsart</h3>
        <span className={styles.blockSub}>
          Einzelvertrag, Sammelvertrag oder Bündelvertrag.
        </span>
        <VertragsartPicker value={vertragsart} onChange={onVertragsartChange} />
      </div>

      {/* Versicherer — erst nach Vertragsart */}
      {vertragsart && (
        <div className={styles.block}>
          <h3 className={styles.blockTitle}>Versicherer</h3>
          <span className={styles.blockSub}>
            Welche Gesellschaft soll den Vertrag führen?
          </span>
          <div className={styles.versichererRow}>
            {VERSICHERER_OPTIONS.map((opt) => {
              const active = versicherer === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onVersichererChange(active ? "" : opt.key)}
                  aria-pressed={active}
                  className={mergeClasses(styles.versichererTile, active && styles.versichererTileActive)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Produkt + Tarifierung — nur Einzelvertrag, erst nach Versicherer */}
      {vertragsart && versicherer && vertragsart === "einzel" && (
        <div className={styles.block}>
          <h3 className={styles.blockTitle}>Produkt und Tarifierungsart</h3>
          <span className={styles.blockSub}>
            Die verfügbaren Produkte hängen von der gewählten Vertragsart ab.
          </span>
          <TarifierungPicker
            value={tarifierung}
            onChange={onTarifierungChange}
            vertragsart={vertragsart}
          />
        </div>
      )}

      {/* Hinweis: Sammel/Bündel haben kein Produkt */}
      {vertragsart && versicherer && (vertragsart === "sammel" || vertragsart === "buendel") && (
        <div className={styles.block}>
          <h3 className={styles.blockTitle}>Produkt und Tarifierungsart</h3>
          <div className={styles.buendelHint}>
            Für {vertragsart === "sammel" ? "Sammelverträge" : "Bündelverträge"} gibt es kein
            gesondertes Produkt — die einzelnen Verträge werden direkt unter{" "}
            „{vertragsart === "sammel" ? "Sammelvertrag" : "Bündelvertrag"}" geführt.
          </div>
        </div>
      )}
    </div>
  );
};

export default VertragsBasisSection;
